import {
  groupCashFlowByCategory,
  sumBalances,
  sumCashFlowByCategory,
} from "./collectionUtils";

export const calculateOperatingCashFlow = (cashFlowData = {}) =>
  sumCashFlowByCategory(cashFlowData.operating || []);

export const calculateFreeCashFlow = (cashFlowData = {}) =>
  calculateOperatingCashFlow(cashFlowData) +
  sumCashFlowByCategory(cashFlowData.investing || []);

export const calculateWorkingCapital = (balanceData = {}) => {
  const currentAssets = sumBalances(
    balanceData.assets?.filter(
      (item) => item.category === "current" || item.type === "current"
    ) || []
  );
  const currentLiabilities = sumBalances(
    balanceData.liabilities?.filter(
      (item) => item.category === "current" || item.type === "current"
    ) || []
  );

  return currentAssets - currentLiabilities;
};

export const calculateCurrentRatio = (balanceData = {}) => {
  const currentAssets = sumBalances(
    balanceData.assets?.filter(
      (item) => item.category === "current" || item.type === "current"
    ) || []
  );
  const currentLiabilities = sumBalances(
    balanceData.liabilities?.filter(
      (item) => item.category === "current" || item.type === "current"
    ) || []
  );

  return currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
};

export const calculateQuickAssets = (balanceData = {}) => {
  let quickAssets = 0;

  (balanceData.assets || []).forEach((item) => {
    const name = item.name?.toLowerCase() || "";
    const type = item.type?.toLowerCase() || "";
    const category = item.category?.toLowerCase() || "";

    if (
      name.includes("نقد") ||
      name.includes("cash") ||
      name.includes("أوراق مالية") ||
      name.includes("marketable") ||
      name.includes("مدين") ||
      name.includes("receivable") ||
      type === "cash" ||
      type === "receivable" ||
      category === "cash" ||
      category === "receivable"
    ) {
      quickAssets += item.balance || item.amount || 0;
    }
  });

  return quickAssets;
};

export const calculateQuickRatio = (balanceData = {}) => {
  const quickAssets = calculateQuickAssets(balanceData);
  const currentLiabilities = sumBalances(
    balanceData.liabilities?.filter(
      (item) => item.category === "current" || item.type === "current"
    ) || []
  );

  return currentLiabilities > 0 ? quickAssets / currentLiabilities : 0;
};

export const calculateCash = (balanceData = {}) => {
  let cash = 0;

  (balanceData.assets || []).forEach((item) => {
    const name = item.name?.toLowerCase() || "";
    const type = item.type?.toLowerCase() || "";

    if (
      name.includes("نقد") ||
      name.includes("cash") ||
      type === "cash" ||
      item.account_type === "cash"
    ) {
      cash += item.balance || item.amount || 0;
    }
  });

  return cash;
};

export const calculateCashEquivalents = (balanceData = {}) => {
  let total = 0;

  (balanceData.assets || []).forEach((item) => {
    const name = item.name?.toLowerCase() || "";
    if (name.includes("cash equivalent") || name.includes("ودائع")) {
      total += item.balance || item.amount || 0;
    }
  });

  return total;
};

export const calculateNetCashFlow = (cashFlowData = {}) =>
  sumCashFlowByCategory(cashFlowData.operating || []) +
  sumCashFlowByCategory(cashFlowData.investing || []) +
  sumCashFlowByCategory(cashFlowData.financing || []);

const calculateCashOutflow = (items = []) =>
  items.reduce(
    (total, item) =>
      total + Math.abs(Math.min(item.amount || item.cash_flow || 0, 0)),
    0
  );

const calculateDailyCashOutflow = (cashFlowData = {}) =>
  calculateCashOutflow(cashFlowData.operating || []) / 30;

export const calculateDaysWorkingCapital = (balanceData = {}, cashFlowData = {}) => {
  const dailyCashOutflow = calculateDailyCashOutflow(cashFlowData);
  const workingCapital = calculateWorkingCapital(balanceData);

  return dailyCashOutflow > 0 ? workingCapital / dailyCashOutflow : 0;
};

export const calculateDefensiveInterval = (balanceData = {}, cashFlowData = {}) => {
  const liquidAssets = calculateQuickAssets(balanceData);
  const dailyCashOutflow = calculateDailyCashOutflow(cashFlowData);

  return dailyCashOutflow > 0 ? liquidAssets / dailyCashOutflow : 0;
};

export const getCurrentAssetsDetails = (balanceData = {}) =>
  (balanceData.assets || []).filter(
    (item) =>
      item.category === "current" ||
      item.type === "current" ||
      item.account_type === "current_asset"
  );

export const getCurrentLiabilitiesDetails = (balanceData = {}) =>
  (balanceData.liabilities || []).filter(
    (item) =>
      item.category === "current" ||
      item.type === "current" ||
      item.account_type === "current_liability"
  );

export const formatCashFlowSection = (items = []) => ({
  total: sumCashFlowByCategory(items),
  items: items.map((item) => ({
    description: item.description || item.name,
    amount: item.amount || item.cash_flow || 0,
    type: item.type || item.category,
  })),
  byCategory: groupCashFlowByCategory(items),
});
